import { onUnmounted, ref, watch, type Ref } from 'vue'

const DEFAULT_DURATION = 550

// Tweens a numeric value from its previous display state to a new target
// whenever the source ref changes. Gives the UI a "slot-machine" feel
// when big numbers update in response to user input.
export function useCountAnimation(source: Ref<number>, duration = DEFAULT_DURATION): Ref<number> {
  const displayed = ref(source.value)
  let rafId: number | null = null
  let startTime = 0
  let fromValue = source.value
  let toValue = source.value

  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  function cancel() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  function tick(now: number) {
    const elapsed = now - startTime
    const progress = Math.min(1, elapsed / duration)
    // ease-out cubic — fast start, soft landing
    const eased = 1 - Math.pow(1 - progress, 3)
    displayed.value = fromValue + (toValue - fromValue) * eased
    if (progress < 1) {
      rafId = requestAnimationFrame(tick)
    } else {
      rafId = null
      displayed.value = toValue
    }
  }

  watch(source, (next) => {
    if (prefersReducedMotion || typeof requestAnimationFrame === 'undefined') {
      displayed.value = next
      return
    }
    cancel()
    fromValue = displayed.value
    toValue = next
    startTime = performance.now()
    rafId = requestAnimationFrame(tick)
  })

  onUnmounted(cancel)

  return displayed
}
